import { useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { App, Button, Card, Col, Descriptions, Flex, Popconfirm, Row, Space, Tabs, Tag, Typography } from 'antd';
import { DeleteOutlined, EditOutlined } from '@ant-design/icons';
import { palette } from '../../app/theme';
import { CardTitle } from '../../components/StatCard';
import type { FieldDefinitionDto } from '@crm/shared';
import { PageHeader } from '../../components/PageHeader';
import { Timeline } from '../../components/Timeline';
import { TaskList } from '../../components/TaskList';
import { useFields } from '../../features/fields/api';
import { contactName, useContact, useDeleteContacts } from '../../features/contacts/api';
import { useSetContactTags, useTags } from '../../features/audience/api';
import { useVerifyContact } from '../../features/verification/api';
import { Select } from 'antd';
import { errorMessage } from '../../api/client';
import { PhotoAvatar } from '../../components/PhotoAvatar';
import { ContactDrawer } from './ContactDrawer';
import { EMAIL_STATUS_LABEL, OrgLink, renderCell } from './ContactsPage';
import { PageSkeleton, RecordError } from '../../components/states';
import { ContactEmails } from '../../components/ContactEmails';
import { DealsTab } from '../../components/DealsTab';
import { ChannelList, emailRows, phoneRows } from '../../components/ContactChannels';
import { splitFieldGroups, ViewMoreCard } from '../../components/ViewMore';
import { AddressList } from '../../components/Addresses';
import { LinksView, toLinks } from '../../components/LinksField';
import { EntityTag } from '../../components/EntityTag';
import { useFeatures } from '../../features/settings/api';

// the top-row cards stretch to the tallest one in the row
const TOP_COL = { display: 'flex', flexDirection: 'column' } as const;
const TOP_CARD = { flex: 1, marginBottom: 24 };

export function ContactProfilePage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { message } = App.useApp();
  const features = useFeatures();
  const contact = useContact(id);
  const defs = useFields('CONTACT');
  const remove = useDeleteContacts();
  const tags = useTags();
  const setTags = useSetContactTags();
  const verify = useVerifyContact();
  const [editing, setEditing] = useState(false);

  // mobile numbers show in the Phones list
  const groups = useMemo(() => splitFieldGroups(defs.data ?? [], (d) => ['first_name', 'last_name', 'mobile'].includes(d.key)), [defs.data]);

  // Basic and Social Media Channels sit above the tabs; every other group stays in the side column
  const topGroups = useMemo(() => {
    const isBasic = ([g]: (typeof groups.main)[number]) => g === 'Basic';
    const isSocial = ([g]: (typeof groups.main)[number]) => /social/i.test(g);
    return { basic: groups.main.find(isBasic), social: groups.main.find(isSocial), rest: groups.main.filter((e) => !isBasic(e) && !isSocial(e)) };
  }, [groups]);

  if (contact.isError) return <RecordError error={contact.error} what="Contact" backTo="/contacts" backLabel="Back to contacts" onRetry={contact.refetch} />;
  if (contact.isLoading || !contact.data) return <PageSkeleton />;
  const c = contact.data;

  const onDelete = async () => {
    try {
      await remove.mutateAsync([c.id]);
      message.success('Contact deleted');
      navigate('/contacts');
    } catch (e) {
      message.error(errorMessage(e));
    }
  };

  const renderFields = (fields: FieldDefinitionDto[]) => (
    <Descriptions className="field-list" column={1} size="small" colon={false} styles={{ label: { width: 130, color: palette.textSecondary }, content: { minWidth: 0, wordBreak: 'break-word' } }}>
      {fields.map((f) => (
        <Descriptions.Item key={f.key} label={f.key === 'email' && c.emails.length > 1 ? 'Emails' : f.key === 'phone' && c.phones.length > 1 ? 'Phones' : f.key === 'organization_id' && c.organizations.length > 1 ? 'Organizations' : f.label}>
          {(f.key === 'organization_id' && c.organizations.length > 1 ? <Flex vertical gap={4} align="flex-start">{c.organizations.map((o) => <OrgLink key={o.id} org={o} />)}</Flex> : f.key === 'email' && c.emails.length ? <ChannelList kind="email" items={emailRows(c.emails)} /> : f.key === 'phone' && c.phones.length ? <ChannelList kind="phone" items={phoneRows(c.phones)} /> : f.type === 'LINKS' ? toLinks(c.fields?.[f.key]).length > 0 && <LinksView value={c.fields?.[f.key]} /> : renderCell(f, c)) || <Typography.Text type="secondary">—</Typography.Text>}
        </Descriptions.Item>
      ))}
    </Descriptions>
  );

  return (
    <div className="page">
      <PageHeader
        breadcrumb={[{ title: 'Contacts', to: '/contacts' }, { title: contactName(c) }]}
        title={contactName(c)}
        subtitle={
          <Space size={4} wrap>
            {c.jobTitle && <span>{c.jobTitle}</span>}
            {c.jobTitle && c.organizations.length > 0 && <span>·</span>}
            {c.organizations.map((o, i) => (
              <span key={o.id}>
                <EntityTag kind="organization" id={o.id}>{o.name}</EntityTag>
                {o.mainContactId === c.id && <Tag style={{ marginLeft: 6, marginInlineEnd: 0 }}>Main contact</Tag>}
              </span>
            ))}
            {c.mergedIntoId && <Tag color="warning">Merged into <Link to={`/contacts/${c.mergedIntoId}`}>another contact</Link></Tag>}
          </Space>
        }
        extra={
          <Space>
            <Button icon={<EditOutlined />} onClick={() => setEditing(true)}>Edit</Button>
            <Popconfirm title="Delete this contact and its history?" okText="Delete" okButtonProps={{ danger: true, loading: remove.isPending }} onConfirm={() => onDelete()}>
              <Button danger icon={<DeleteOutlined />}>Delete</Button>
            </Popconfirm>
          </Space>
        }
      />
      <Row gutter={24} align="stretch">
        <Col xs={24} lg={8} style={TOP_COL}>
          <Card style={TOP_CARD}>
            <Space align="center" size={14}>
              <PhotoAvatar kind="contact" recordId={c.id} fileId={c.photoFileId} fallback={(c.firstName?.[0] ?? c.email?.[0] ?? '?').toUpperCase()} />
              <div>
                <div style={{ fontWeight: 600, fontSize: 16 }}>{contactName(c)}</div>
                <div>{c.email ? <Typography.Text copyable>{c.email}</Typography.Text> : <Typography.Text type="secondary">No email</Typography.Text>}</div>
                <Space size={4} style={{ marginTop: 4 }} wrap>
                  {defs.data && renderCell(defs.data.find((d) => d.key === 'email_status')!, c)}
                  {c.email && (
                    <Button size="small" type="link" style={{ padding: 0, height: 'auto' }} loading={verify.isPending} onClick={() => verify.mutateAsync({ id: c.id }).then((r) => message.success(`Email is ${(EMAIL_STATUS_LABEL[r.status] ?? r.status).toLowerCase()}${r.reason ? ` (${r.reason})` : ''}`)).catch((e) => message.error(errorMessage(e)))}>
                      Verify
                    </Button>
                  )}
                  {!c.subscribed && <Tag color="warning">Unsubscribed</Tag>}
                </Space>
              </div>
            </Space>
            <div style={{ marginTop: 20 }}>
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>Tags</Typography.Text>
              <Select
                mode="multiple"
                optionFilterProp="label"
                style={{ width: '100%', marginTop: 6 }}
                placeholder="Add tags"
                value={c.tags.map((t) => t.id)}
                loading={setTags.isPending}
                onChange={(tagIds) => setTags.mutateAsync({ id: c.id, tagIds }).catch((e) => message.error(errorMessage(e)))}
                options={(tags.data ?? []).map((t) => ({ value: t.id, label: t.path }))}
                tagRender={({ label, value, closable, onClose }) => {
                  const t = c.tags.find((x) => x.id === value) ?? tags.data?.find((x) => x.id === value);
                  return <Tag color={t?.color ?? undefined} closable={closable} onClose={onClose} style={{ marginInlineEnd: 4 }}>{label}</Tag>;
                }}
              />
            </div>
            {(c.lists.length > 0 || c.segments.length > 0) && (
              <Space orientation="vertical" size={10} style={{ marginTop: 14, width: '100%' }}>
                {c.lists.length > 0 && (
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Lists</Typography.Text>
                    <Flex wrap gap={6} style={{ marginTop: 6 }}>
                      {c.lists.map((l) => <EntityTag key={l.id} kind="list" id={l.id}>{l.name}</EntityTag>)}
                    </Flex>
                  </div>
                )}
                {c.segments.length > 0 && (
                  <div>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>Segments</Typography.Text>
                    <Flex wrap gap={6} style={{ marginTop: 6 }}>
                      {c.segments.map((s) => <EntityTag key={s.id} kind="segment" id={s.id}>{s.name}</EntityTag>)}
                    </Flex>
                  </div>
                )}
              </Space>
            )}
          </Card>
        </Col>
        {[topGroups.basic, topGroups.social].map((entry) => entry && (
          <Col key={entry[0]} xs={24} md={12} lg={8} style={TOP_COL}>
            <Card title={<CardTitle title={entry[0]} />} style={TOP_CARD}>
              {renderFields(entry[1])}
            </Card>
          </Col>
        ))}
      </Row>
      <Row gutter={24}>
        <Col xs={24} lg={8}>
          <Card title={<CardTitle title="Addresses" />} style={{ marginBottom: 24 }}>
            <AddressList items={c.addresses} />
          </Card>
          {topGroups.rest.map(([group, fields]) => (
            <Card key={group} title={<CardTitle title={group} />} style={{ marginBottom: 24 }}>
              {renderFields(fields)}
            </Card>
          ))}
          <ViewMoreCard groups={groups.more} render={renderFields} />
        </Col>
        <Col xs={24} lg={16}>
          <Card styles={{ body: { paddingTop: 8 } }}>
            <Tabs
              items={[
                { key: 'timeline', label: 'Timeline', children: <Timeline contactId={c.id} fields={defs.data} /> },
                { key: 'tasks', label: `Tasks${c.counts.openTasks ? ` (${c.counts.openTasks})` : ''}`, children: <TaskList contactId={c.id} /> },
                ...(features.enabled('pipelines') ? [{ key: 'deals', label: `Deals (${c.counts.deals})`, children: <DealsTab contactId={c.id} /> }] : []),
                { key: 'emails', label: `Emails (${c.counts.threads})`, children: <ContactEmails contactId={c.id} contactEmail={c.email} contactName={contactName(c)} /> },
              ]}
            />
          </Card>
        </Col>
      </Row>
      <ContactDrawer open={editing} onClose={() => setEditing(false)} defs={defs.data ?? []} contact={c} />
    </div>
  );
}
